/**
 * Shared Web Audio context + decoded-buffer loader.
 *
 * Audio bytes are resolved via the generic asset loader (bundled-first,
 * server-fallback — see ../asset.ts) and decoded once. Decoded AudioBuffers are
 * memoised for the session so repeated playback is instant; raw bytes rely on
 * the HTTP cache for persistence (no manual caching layer).
 */
import { loadAsset } from '../asset';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;
/** True while we've parked the context on purpose for a push-to-talk window, so
 *  the error-recovery net below doesn't fight us and wake it mid-SCO. */
let duplexSuspended = false;
let contextRecreating = false;

function resetAudioContext(): void {
  if (contextRecreating) return;
  contextRecreating = true;
  try {
    ctx?.close?.();
  } catch {
    // ignore close errors and rebuild anyway
  }
  ctx = null;
  master = null;
  keepAlive = null;
  contextRecreating = false;
}

export function audioContext(): AudioContext {
  if (!ctx) {
    const c = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    c.addEventListener('statechange', () => {
      if (c.state === 'interrupted' || c.state === 'suspended') {
        if (!duplexSuspended) void c.resume(); 
      }
    });
    c.addEventListener('error', () => {
      if (!duplexSuspended) {
        resetAudioContext();
      }
    });
    ctx = c;
  }
  return ctx;
}

/**
 * The single master output node. All playback (voice + SFX) connects here
 * instead of straight to `ctx.destination`, so the whole client's audio can be
 * muted in one place — used by the play-test harness to "solo" one iframe.
 */
export function masterOut(): AudioNode {
  const c = audioContext();
  if (!master) {
    master = c.createGain();
    master.gain.value = muted ? 0 : 1;
    master.connect(c.destination);
  }
  return master;
}

/** Globally mute/unmute all client audio (play-test harness uses this). */
export function setAudioMuted(m: boolean): void {
  muted = m;
  if (master) master.gain.value = m ? 0 : 1;
}

/**
 * Resume the context and report whether it's now actually running. On most
 * browsers this must be called from a user gesture; the Android WebView allows
 * autoplay (mediaPlaybackRequiresUserGesture=false), so it resumes on startup too.
 */
export async function resumeAudio(): Promise<boolean> {
  const c = audioContext();
  if (c.state === 'suspended' || c.state === 'interrupted') {
    try {
      await c.resume();
    } catch {
      /* will retry on next gesture */
    }
  }
  if (c.state !== 'running') {
    resetAudioContext();
    const fresh = audioContext();
    try {
      await fresh.resume();
    } catch {
      /* will retry on next gesture */
    }
  }
  startKeepAlive();
  return c.state === 'running';
}

/**
 * Park (`true`) then unpark (`false`) all WebAudio around a push-to-talk window.
 * Parking releases the output stream, so the A2DP→SCO route switch has nothing
 * live to invalidate — which is what was killing the context and silencing the
 * whole app. Playing sounds freeze and resume at the same offset on unpark.
 */
export function suspendForDuplex(park: boolean): void {
  duplexSuspended = park;
  const c = audioContext();
  if (park) {
    void c.suspend();
  } else {
    void c.resume().catch(() => {
      resetAudioContext();
      void audioContext().resume();
    });
  }
}

let keepAlive: AudioBufferSourceNode | null = null;

/**
 * Keep a continuous, near-silent signal flowing to the output so the OS never
 * tears the audio route down between voice cues.
 *
 * Without this, the first sound after a quiet stretch loses its head while the
 * route is re-acquired — for a Bluetooth A2DP link that cold-start is easily
 * 100–300 ms, which is exactly the "cut off at the beginning" symptom. Players
 * wear headsets and we run voice recognition continuously, so we never want the
 * media route to drop and have to be re-established mid-game.
 *
 * The signal is ~1e-4 (−80 dBFS) white noise: inaudible even on headphones, but
 * non-zero so the stream isn't optimised away as digital silence. Routed through
 * masterOut, so a gain-0 mute still renders (route stays warm) and the play-test
 * "solo" doesn't make it audible. Idempotent; started once audio is unlocked.
 */
function startKeepAlive(): void {
  if (keepAlive) return;
  const c = audioContext();
  // 1 s of very low-amplitude noise, looped forever.
  const buf = c.createBuffer(1, c.sampleRate, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 1e-4;
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  src.connect(masterOut());
  src.start();
  keepAlive = src;
}

const buffers = new Map<string, Promise<AudioBuffer | null>>();

/**
 * Load + decode an audio asset by relative path (e.g. `voice/scan.mp3`),
 * memoised. Returns null on failure so callers can skip gracefully.
 */
export function loadAudio(path: string): Promise<AudioBuffer | null> {
  let p = buffers.get(path);
  if (!p) {
    p = (async () => {
      const bytes = await loadAsset(path);
      if (!bytes) return null;
      try {
        return await audioContext().decodeAudioData(bytes.slice(0));
      } catch {
        return null;
      }
    })();
    buffers.set(path, p);
  }
  return p;
}

export const voicePath = (token: string): string => `voice/${token}.mp3`;
export const sfxPath = (name: string): string => `sfx/${name}.opus`;
